import { useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { isDefined } from '@e-socna/utils';

import { graphql } from '@e-socna/data-layer';
import { EMPTY_ID } from '@e-socna/empty-client';
import type { Id } from '@e-socna/data-layer';

import { Breadcrumbs } from '../../components/Breadcrumbs';
import {
  FileExplorer,
  FileExplorerStrategy,
  useFilesPageState,
} from '../../components/FileExplorer';
import { Loading } from '../../components/Loading';
import {
  PATH_MISSION_DEFAULT,
  PATH_MISSION_FILES_DEFAULT,
} from '../../app/constants/paths.constant';
import { useMissionContext } from '../../contexts/MissionContext/hooks/useMissionContext.hook';
import { useBuildPaths } from '../../hooks/useBuildPaths.hook';
import type { Mode } from '../../components/FileExplorer/types/mode.type';

import { MissionPage_OneMission_QueryDocument } from '../Mission/hooks/useFindOneMissionQuery.hook';
import { useMissionBreadcrumbPaths } from '../../hooks/useMissionBreadcrumbPaths.hook';
import { useMissionSpaceWording } from '../../hooks/useMissionSpaceWording.hook';
import { buildDocumentTitle } from '../../utils/document-title.util';
import { PageTitle } from '../../components/PageTitle';

import { TUS_CATEGORY_MISSION_FILES } from './constants/tus.constant';

const MissionFilesPage_FileDirectory_QueryDocument = graphql(`
  query MissionFilesPage_FileDirectory_Query($missionId: Id!) {
    findOneMission(id: $missionId) {
      _id
      fileDirectory {
        _id
        allowedFileCategories {
          _id
          hideInFileExplorer
          label
          parentCategoryId
        }
        fileCategoryAccesses {
          ...FileExplorer_FileCategoryAccess_Fragment
        }
        files {
          _id
          categoryId
          originalName
          mimeType
          size
          uri
          compressedVariantId
          thumbnailVariantId
          hideInFileExplorer
        }
      }
    }
  }
`);

const MissionFilesPage_DeleteFiles_MutationDocument = graphql(`
  mutation MissionFilesPage_DeleteFiles_Mutation(
    $entityId: Id!
    $fileIds: [Id!]!
  ) {
    deleteMissionFiles(entityId: $entityId, fileIds: $fileIds)
  }
`);

const MissionFilesPage_MoveFiles_MutationDocument = graphql(`
  mutation MissionFilesPage_MoveFiles_Mutation(
    $entityId: Id!
    $destinationFileCategoryId: Id!
    $fileIds: [Id!]!
  ) {
    moveMissionFiles(
      entityId: $entityId
      destinationFileCategoryId: $destinationFileCategoryId
      fileIds: $fileIds
    )
  }
`);

const MissionFilesPage_RenameFile_MutationDocument = graphql(`
  mutation MissionFilesPage_RenameFile_Mutation(
    $entityId: Id!
    $fileId: Id!
    $newOriginalName: NonEmptyString!
  ) {
    renameMissionFile(
      entityId: $entityId
      fileId: $fileId
      newOriginalName: $newOriginalName
    )
  }
`);

export const MissionFilesPage = () => {
  const context = useMissionContext();
  const missionWording = useMissionSpaceWording();
  const missionBreadcrumbPaths = useMissionBreadcrumbPaths();

  const { paths: missionPaths } = useBuildPaths(
    { default: PATH_MISSION_DEFAULT, files: PATH_MISSION_FILES_DEFAULT },
    { folderId: context.folderId, missionId: context.missionId },
  );

  // Must be defined before any conditional return (hooks rule).
  const buildFilesPath = useCallback(
    (fileCategoryId: Id, mode: Mode, fileId?: Id | null): string => {
      const base = `${missionPaths.files}/${String(fileCategoryId)}/${mode}`;
      return isDefined(fileId) ? `${base}/${String(fileId)}` : base;
    },
    [missionPaths.files],
  );

  const {
    allowedFileCategories,
    fileCategoryAccesses,
    files,
    fileCountsByCategoryId,
    isLoadingFileDirectory,
    isDeletingFiles,
    isMovingFiles,
    isRenamingFile,
    queryDocumentNamesToInvalidate,
    handleDeleteFiles,
    handleMoveFiles,
    handleRenameFile,
  } = useFilesPageState({
    entityId: context.missionId,
    buildFilesPath,
    queryDocument: MissionFilesPage_FileDirectory_QueryDocument,
    queryVariables: { missionId: context.missionId ?? EMPTY_ID },
    selectFileDirectory: (data) => data.findOneMission.fileDirectory,
    deleteFilesMutationDocument: MissionFilesPage_DeleteFiles_MutationDocument,
    moveFilesMutationDocument: MissionFilesPage_MoveFiles_MutationDocument,
    renameFileMutationDocument: MissionFilesPage_RenameFile_MutationDocument,
    relatedQueryDocumentsToInvalidate: [MissionPage_OneMission_QueryDocument],
  });

  const breadcrumbs = (
    <>
      <Helmet>
        <title>
          {buildDocumentTitle(
            `Liste des Fichiers ${missionWording.ofName}`,
            context.mission?.code,
          )}
        </title>
      </Helmet>
      <Breadcrumbs
        paths={[
          ...missionBreadcrumbPaths,
          { key: 'files', label: 'fichiers', to: missionPaths.files },
        ]}
      />
    </>
  );

  if (!context.mission) {
    return (
      <>
        <Loading />
        {breadcrumbs}
        <PageTitle title={`Liste des Fichiers ${missionWording.ofName}`} />
      </>
    );
  }

  return (
    <>
      {breadcrumbs}
      <PageTitle
        code={context.mission.code}
        title={context.mission.consolidation.title}
      />
      <FileExplorer
        allowedFileCategories={allowedFileCategories}
        buildPath={buildFilesPath}
        entityId={context.missionId}
        entityLabel={`Mission ${context.mission.code}`}
        fileCategoryAccesses={fileCategoryAccesses}
        fileCountsByCategoryId={fileCountsByCategoryId}
        files={files}
        filesQueryDocumentNamesToInvalidate={queryDocumentNamesToInvalidate}
        isDeletingFiles={isDeletingFiles}
        isLoadingCategories={isLoadingFileDirectory}
        isLoadingFiles={isLoadingFileDirectory}
        isMovingFiles={isMovingFiles}
        isRenamingFile={isRenamingFile}
        onDeleteFiles={handleDeleteFiles}
        onMoveFiles={handleMoveFiles}
        onRenameFile={handleRenameFile}
        strategy={FileExplorerStrategy.RouterConnected}
        tusCategory={TUS_CATEGORY_MISSION_FILES}
      />
    </>
  );
};
